import ClearIcon from '@mui/icons-material/Clear';
// import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  InputLabel as Label,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import InputLabel from 'components/Input';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import { changePageAction } from 'store/actions/dashboardActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Scope from 'types/entities/Scope';
import CloseIcon from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';

interface SimpleIdNameOption {
  id: number;
  name: string;
}

type ISearchHeaderCellOptions = SimpleIdNameOption[];
// type ISearchHeaderCellOptions = Scope[] | undefined;

type SearchHeaderCellProps = {
  field: string;
  options?: ISearchHeaderCellOptions;
  headerName: string;
  disabled?: boolean;
};
const SearchHeaderCell = React.memo((props: SearchHeaderCellProps) => {
  const { field, options, headerName, disabled = false } = props;

  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const filter = useSelector((state) => state.filter);
  const searchValue = filter.selected?.[field]?.[0] ?? '';
  const [inputValue, setInputValue] = useState(searchValue);
  const [textValue, setTextValue] = useState(searchValue);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchUpdate(field, inputValue);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (searchValue) setInputValue(searchValue);
  }, [searchValue]);

  const searchType = useCallback(() => {
    switch (field) {
      case 'id':
      case 'purchase_order':
      case 'mission_advancement_id':
      case 'workunit_reference':
      case 'clients':
      case 'overdue_by':
      case 'label':
        return 'select';
      case 'forecast_date':
      case 'delivery_date':
        return 'date';
      default:
        return 'text';
    }
  }, [field]);

  const searchUpdate = useCallback(
    (field: string, value: any) => {
      if (field && value) {
        dispatch(selectFilterAction({ [field]: [value] }));
        if (field === 'clients') {
          dispatch(selectFilterRemoveAction({ otp: [] }));
          dispatch(changePageAction(0));
        }
        return;
      }
      if (field === 'overdue_by') {
        dispatch(selectFilterAction({ [field]: ['High to low'] }));
        return;
      }
      if (field === 'clients' || field === 'search') {
        dispatch(selectFilterRemoveAction({ [field]: [] }));
        dispatch(changePageAction(0));
        return;
      }
      dispatch(selectFilterAction({ [field]: [] }));
    },
    [dispatch]
  );

  const handleSearchClick = (field: string) => {
    searchUpdate(field, textValue);
  };

  const handleClearClick = (field: string) => {
    // setInputValue('');
    setTextValue('');
    searchUpdate(field, '');
  };

  const renderInput = () => {
    if (searchType() === 'select' && options) {
      return (
        <FormControl fullWidth>
          <label htmlFor={`select_search_${field}`}>{t(headerName)}</label>
          <Select
            labelId={`select_search_${field}`}
            id={`$current_${field}`}
            disabled={disabled}
            placeholder={t(field)}
            onChange={(e) => {
              e.stopPropagation();
              setInputValue(e.target.value);
            }}
            // defaultValue={field === 'overdue_by' ? 'High to low' : ''}
            onBlur={(e) => e.stopPropagation()}
            value={inputValue || ''}
            fullWidth
            input={
              <OutlinedInput
                endAdornment={
                  inputValue && field !== 'overdue_by' ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="medium"
                        // edge="end"
                        sx={{ zIndex: 1, mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setInputValue('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
            }
            componentsProps={{}}
            sx={{
              '.MuiSelect-select': {
                p: 0.5,
              },
              '.MuiSelect-icon': {
                position: 'absolute',
                right: 8, // dropdown arrow position
                zIndex: 2, // below close icon
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300, // Limits the height of the dropdown list
                  '& .MuiMenuItem-root': {
                    minHeight: 32, // Reduces the height of each item
                    fontSize: '0.875rem',
                    py: 0.5, // Slimmer vertical padding
                  },
                },
              },
            }}
          >
            {field === 'clients' ? <MenuItem value="ALL">ALL</MenuItem> : null}
            {options?.map((option) => (
              <MenuItem value={option.id}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <>
        <label htmlFor={`input_header_${field}`}>{t(headerName)}</label>
        <TextField
          onChange={(newValue) => {
            setTextValue(newValue.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Prevent the default form submission or newline
              e.preventDefault(); // Trigger the search action
              handleSearchClick(field);
            }
          }}
          id={`input_header_${field}`}
          value={textValue}
          type={searchType()}
          required
          variant="standard"
          placeholder={t('Search')}
          size="medium"
          fullWidth
          InputProps={{
            // Ensure the Adornment is set for the END position for right-side icons
            endAdornment: (
              // Consolidate logic inside a single InputAdornment
              <InputAdornment position="end">
                {textValue && (
                  <IconButton onClick={() => handleClearClick('search')} color="primary" sx={{ p: '4px' }}>
                    <ClearIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                )}

                <IconButton onClick={() => handleSearchClick('search')}>
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
            // Input element style
            style: { fontSize: 12, height: 32 },
          }}
          sx={{
            mt: 3,
            // TARGET THE ROOT ELEMENT (MuiInputBase-root) for the border and radius
            '& .MuiInputBase-root': {
              height: 32,
              border: '1px solid #C4C4D9',
              borderRadius: '4px',
              // Important for standard variant: remove default bottom border/underline
              '&:before, &:after': {
                borderBottom: 'none !important',
              },
              // Add padding to the right of the input to make space for the Adornment
              paddingRight: '5px !important',
            },
            // Change placeholder font size
            '& .MuiInputBase-input::placeholder': {
              fontSize: '1rem',
              fontWeight: 500,
            },
          }}
        />
      </>
    );
  };

  return <Box sx={{ display: 'block', width: '100%' }}>{renderInput()}</Box>;
});

export default SearchHeaderCell;
